import { useState, useEffect } from 'react';
import { X, Search, Calendar, MapPin } from 'lucide-react';
import api from '../lib/api';

/**
 * SharePlanPicker — full-screen sheet to pick one of MY plans
 * (hosted or upcoming joined) and share into the current chat.
 */
export default function SharePlanPicker({ onClose, onPick }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/plans/me/upcoming');
        setPlans(Array.isArray(data) ? data : []);
      } catch {/* fallback: search */}
      setLoading(false);
    })();
  }, []);

  const filtered = q.trim()
    ? plans.filter(p => (p.title || '').toLowerCase().includes(q.toLowerCase()) || (p.city || '').toLowerCase().includes(q.toLowerCase()))
    : plans;

  return (
    <div className="fixed inset-0 z-[10070] bg-black/40 flex items-end justify-center animate-fade-in" onClick={onClose} data-testid="share-plan-overlay">
      <div className="w-full max-w-[480px] h-[80vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()} data-testid="share-plan-sheet">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-black/[0.04]">
          <button onClick={onClose} className="text-sm font-semibold text-[#9B9EA3]">Cancel</button>
          <p className="text-base font-bold text-[#111111]">Share a plan</p>
          <span className="w-12" />
        </div>
        <div className="flex-shrink-0 px-4 py-2">
          <div className="bg-[#F1F0EA] rounded-full flex items-center gap-2 px-3.5 py-2.5">
            <Search size={16} className="text-[#9B9EA3]" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search your plans" data-testid="share-plan-search" className="flex-1 bg-transparent text-sm focus:outline-none placeholder-[#B0B3B8]" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto hide-scrollbar px-3 pb-4">
          {loading && <p className="text-center text-xs text-[#9B9EA3] py-6">Loading your plans…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-xs text-[#9B9EA3] py-6">{q ? 'No plans match.' : "You don't have any upcoming plans yet."}</p>
          )}
          <div className="space-y-1.5">
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => onPick(p)}
                data-testid={`share-plan-row-${p.id}`}
                className="w-full flex items-center gap-3 p-2.5 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
              >
                <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[#F1F0EA] flex-shrink-0 flex items-center justify-center">
                  {p.cover_image ? <img loading="lazy" decoding="async" src={p.cover_image} alt="" className="w-full h-full object-cover" /> : <Calendar size={22} className="text-[#9B9EA3]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#111111] truncate">{p.title}</p>
                  <p className="text-[11px] text-[#9B9EA3] truncate mt-0.5 flex items-center gap-1">
                    <MapPin size={10} /> {[p.venue, p.city].filter(Boolean).join(', ')}
                  </p>
                  <p className="text-[11px] text-[#6B6E73] truncate">{p.date} · {p.start_time}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
